import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { ArrowLeft, ArrowRight, Building, Eye, EyeOff, Loader2, ShieldCheck, UserRound } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { DatePicker } from '@/components/ui/date-picker';
import { api } from '@/lib/api/client';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Stepper, useStepper } from '@/components/ui/stepper';
import { Textarea } from '@/components/ui/textarea';
import { PageHeader } from '@/components/feedback/PageHeader';
import { ScrollPage } from '@/components/layout/PageContainer';
import { formatDate } from '@/lib/utils/date';
import { useAuthStore } from '@/lib/auth/store';
import { useRegisterUser } from '../api';
import { useAclSettings } from '../lookups/api';
import { toLegacyExpiry } from '../format';
import { UsernameAvailability, type UsernameStatus } from '../components/UsernameAvailability';
import {
  createSubSuperAdminSchema,
  type CreateSubSuperAdminInput,
} from '../schema';

/**
 * Field groups per step — drives both rendering and per-step validation.
 * The Review step has no fields to validate; the entire schema's already
 * been validated by the time the user advances past Step 2.
 */
const STEP_FIELDS: Record<number, Array<keyof CreateSubSuperAdminInput>> = {
  0: ['username', 'password', 'confirmPassword', 'name', 'emailid', 'number'],
  1: ['company', 'address', 'pincode'],
  2: ['accountType', 'expiryDate', 'aclId'],
  3: [],
};

const STEPS = [
  { id: 'account', label: 'Account', description: 'Login & contact', icon: UserRound },
  { id: 'org', label: 'Organization', description: 'Company & address', icon: Building },
  { id: 'access', label: 'Access', description: 'Validity & ACL scope', icon: ShieldCheck },
  { id: 'review', label: 'Review', description: 'Confirm details' },
] as const;

export function CreateSubSuperAdminPage() {
  const navigate = useNavigate();
  const parentId = Number(useAuthStore((s) => s.userId));
  const [showPassword, setShowPassword] = useState(false);

  const aclQuery = useAclSettings(parentId);
  const aclOptions = aclQuery.data ?? [];

  const register = useRegisterUser();
  const stepper = useStepper(STEPS.length);
  const [themeColor, setThemeColor] = useState('#2c5282');
    const [sidebarPosition, setSidebarPosition] = useState<'left' | 'right' | 'top'>('left');

  const form = useForm<CreateSubSuperAdminInput>({
    resolver: zodResolver(createSubSuperAdminSchema),
    mode: 'onTouched',
    defaultValues: {
      username: '',
      password: '',
      confirmPassword: '',
      name: '',
      emailid: '',
      number: '',
      company: '',
      address: '',
      pincode: '',
      accountType: '0',
      expiryDate: '',
      aclId: '',
    },
  });

  const [usernameStatus, setUsernameStatus] = useState<UsernameStatus>('idle');
  const usernameValue = form.watch('username');

  const handleNext = async () => {
    if (stepper.current === 0 && (usernameStatus === 'unavailable' || usernameStatus === 'checking')) {
      if (usernameStatus === 'unavailable') {
        form.setError('username', { message: 'Username is not available' });
      }
      return;
    }
    const fields = STEP_FIELDS[stepper.current];
    if (!fields || fields.length === 0) {
      stepper.next();
      return;
    }
    const ok = await form.trigger(fields);
    if (ok) stepper.next();
  };

  const onSubmit = (input: CreateSubSuperAdminInput) => {
    register.mutate(
      {
        username: input.username.trim(),
        password: input.password,
        name: input.name.trim(),
        emailid: input.emailid.trim(),
        number: input.number.trim(),
        company: input.company.trim(),
        address: input.address.trim(),
        pincode: input.pincode.trim(),
        parent: parentId,
        accountType: Number(input.accountType) as 0 | 1 | 3,
        userType: 'sub_sadmin',
        expiryDate: toLegacyExpiry(input.expiryDate),
        // Legacy UserInfo defaults — see CreateUserPage comment.
        sub: 0,
        userId2: 0,
        settings: {},
        aclId: Number(input.aclId),
      },
      {
        onSuccess: async (res) => {
  if (res?.message && res.message.toLowerCase().includes('exist')) {
    form.setError('username', { message: res.message });
    stepper.goTo(0);
    return;
  }
  toast.success(res?.message ?? 'Sub Super Admin created');
  const newUserId = (res as { userId?: string })?.userId;
  if (newUserId && newUserId !== '-1') {
    try {
            // Save custom themeColor + sidebarPosition
      await api.post(`/api/obd/user/${newUserId}/ui/settings`, {
        json: JSON.stringify({ themeColor, sidebarPosition }),
      });
    } catch {
      // Silent fail — UI settings copy is best-effort
    }
  }
  navigate('/sub-super-admin/user-list');
},
onError: (err) => {
  toast.error(readErrorMessage(err) ?? 'Could not create the user.');
},
      },
    );
  };

  return (
    <ScrollPage>
      <PageHeader
        title="Create Sub Super Admin"
        description="Create a permission-scoped admin. The ACL setting controls which modules they can access."
        actions={
          <Button asChild variant="outline">
            <Link to="/sub-super-admin/user-list">
              <ArrowLeft className="h-4 w-4" />
              Back to list
            </Link>
          </Button>
        }
      />

      <Card>
        <CardContent className="p-6">
          <Stepper
            steps={[...STEPS]}
            current={stepper.current}
            completed={stepper.completed}
            onStepClick={(i) => i <= stepper.current && stepper.goTo(i)}
          />
        </CardContent>
      </Card>

      <Form {...form}>
          <form className="space-y-4" noValidate>
          {stepper.current === 0 && (
            <Card>
              <CardHeader>
                <CardTitle>Account</CardTitle>
                <CardDescription>
                  Login credentials and direct contact for this admin.
                </CardDescription>
              </CardHeader>
              <CardContent className="grid gap-4 md:grid-cols-2">
                <FormField
                  control={form.control}
                  name="username"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Username</FormLabel>
                      <FormControl>
                        <Input {...field} autoComplete="off" placeholder="jane.doe" />
                      </FormControl>
                      <UsernameAvailability
                        username={usernameValue}
                        onStatusChange={setUsernameStatus}
                      />
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Full name</FormLabel>
                      <FormControl>
                        <Input {...field} autoComplete="name" placeholder="Jane Doe" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="password"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Password</FormLabel>
                      <FormControl>
                        <div className="relative">
                          <Input
                            {...field}
                            type={showPassword ? 'text' : 'password'}
                            autoComplete="new-password"
                            className="pr-9"
                          />
                          <button
                            type="button"
                            onClick={() => setShowPassword((v) => !v)}
                            className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-1 text-muted-foreground hover:text-foreground"
                            aria-label={showPassword ? 'Hide password' : 'Show password'}
                          >
                            {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                          </button>
                        </div>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="confirmPassword"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Confirm password</FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          type={showPassword ? 'text' : 'password'}
                          autoComplete="new-password"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="emailid"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Email</FormLabel>
                      <FormControl>
                        <Input {...field} type="email" autoComplete="email" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                                <FormField
                  control={form.control}
                  name="number"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Phone</FormLabel>
                      <FormControl>
                        <Input {...field} type="tel" autoComplete="tel" placeholder="+91 9876543210" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <div className="md:col-span-2 space-y-4 rounded-lg border p-4">
                  <h4 className="text-sm font-semibold tracking-tight">UI Appearance</h4>
                  <div className="grid gap-4 md:grid-cols-2">
                    <div className="space-y-1.5">
                      <label className="text-sm font-medium leading-none">Brand Color</label>
                      <div className="flex items-center gap-3">
                        <input
                          type="color"
                          value={themeColor}
                          onChange={(e) => setThemeColor(e.target.value)}
                          title="Choose brand color"
                          className="h-10 w-12 cursor-pointer rounded-md border border-input p-1 shadow-sm transition-colors hover:border-ring focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                        />
                        <span className="text-xs text-muted-foreground font-mono">{themeColor}</span>
                      </div>
                    </div>
                    <div className="space-y-1.5">
                      <label className="text-sm font-medium leading-none">Sidebar Position</label>
                      <RadioGroup
                        value={sidebarPosition}
                        onValueChange={(v) => setSidebarPosition(v as 'left' | 'right' | 'top')}
                        className="grid grid-cols-3 gap-2 pt-1"
                      >
                        {([
                          { value: 'left', label: 'Left' },
                          { value: 'top', label: 'Top' },
                          { value: 'right', label: 'Right' },
                        ] as const).map((opt) => (
                          <label
                            key={opt.value}
                            className="flex cursor-pointer items-center gap-2 rounded-md border bg-card px-3 py-2 text-sm hover:border-ring [&:has([data-state=checked])]:border-primary [&:has([data-state=checked])]:bg-primary/5"
                          >
                            <RadioGroupItem value={opt.value} />
                            <span className="font-medium">{opt.label}</span>
                          </label>
                        ))}
                      </RadioGroup>
                    </div>
                  </div>
                </div>

              </CardContent>
            </Card>
          )}

          {stepper.current === 1 && (
            <Card>
              <CardHeader>
                <CardTitle>Organization</CardTitle>
                <CardDescription>Company and mailing address.</CardDescription>
              </CardHeader>
              <CardContent className="grid gap-4 md:grid-cols-2">
                <FormField
                  control={form.control}
                  name="company"
                  render={({ field }) => (
                    <FormItem className="md:col-span-2">
                      <FormLabel>Company</FormLabel>
                      <FormControl>
                        <Input {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="address"
                  render={({ field }) => (
                    <FormItem className="md:col-span-2">
                      <FormLabel>Address</FormLabel>
                      <FormControl>
                        <Textarea {...field} rows={3} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="pincode"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Pincode</FormLabel>
                      <FormControl>
                        <Input {...field} inputMode="numeric" maxLength={10} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </CardContent>
            </Card>
          )}

          {stepper.current === 2 && (
            <Card>
              <CardHeader>
                <CardTitle>Access</CardTitle>
                <CardDescription>
                  Billing mode, validity, and the ACL set that scopes which modules this admin can
                  manage.
                </CardDescription>
              </CardHeader>
              <CardContent className="grid gap-4 md:grid-cols-2">
                <FormField
                  control={form.control}
                  name="accountType"
                  render={({ field }) => (
                    <FormItem className="md:col-span-2">
                      <FormLabel>Account type</FormLabel>
                      <FormControl>
                        <RadioGroup
                          value={field.value}
                          onValueChange={field.onChange}
                          className="grid grid-cols-2 gap-3"
                        >
                          {(
                            [
                              { value: '0', label: 'Promotional', hint: 'Lower priority delivery' },
                              { value: '1', label: 'Transactional', hint: 'Higher priority delivery' },
                              { value: '3', label: 'Both', hint: 'Promotional + Transactional' },
                            ] as const
                          ).map((opt) => (
                            <label
                              key={opt.value}
                              className="flex cursor-pointer items-start gap-3 rounded-md border bg-card p-3 hover:border-ring"
                            >
                              <RadioGroupItem value={opt.value} className="mt-0.5" />
                              <div>
                                <div className="text-sm font-medium">{opt.label}</div>
                                <div className="text-xs text-muted-foreground">{opt.hint}</div>
                              </div>
                            </label>
                          ))}
                        </RadioGroup>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="expiryDate"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Validity expires on</FormLabel>
                      <FormControl>
                        <DatePicker
                          value={field.value ? new Date(field.value) : null}
                          onChange={(d) => field.onChange(d ? d.toISOString() : '')}
                          fromDate={new Date()}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="aclId"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>ACL setting</FormLabel>
                      <Select
                        value={field.value}
                        onValueChange={field.onChange}
                        disabled={aclQuery.isLoading}
                      >
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue
                              placeholder={aclQuery.isLoading ? 'Loading…' : 'Pick an ACL set'}
                            />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {aclOptions.length === 0 ? (
                            <div className="px-3 py-2 text-xs text-muted-foreground">
                              No ACL sets available. Create one under Access Control first.
                            </div>
                          ) : (
                            aclOptions.map((opt) => (
                              <SelectItem key={opt.aclId} value={String(opt.aclId)}>
                                {opt.aclName}
                              </SelectItem>
                            ))
                          )}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                  />
              </CardContent>
            </Card>
          )}

          {stepper.current === 3 && (
            <ReviewStep form={form} aclLookup={Object.fromEntries(aclOptions.map((o) => [String(o.aclId), o.aclName]))} />
          )}

          {/* Sticky footer with navigation buttons */}
          <div className="sticky bottom-0 -mx-4 mt-2 flex items-center justify-between gap-3 border-t bg-background/95 px-4 py-3 backdrop-blur lg:-mx-8 lg:px-8">
            <Button
              type="button"
              variant="ghost"
              onClick={stepper.prev}
              disabled={stepper.isFirst || register.isPending}
            >
              <ArrowLeft className="h-4 w-4" />
              Previous
            </Button>

            <div className="text-xs text-muted-foreground">
              Step {stepper.current + 1} of {STEPS.length}
            </div>

            <div className="flex items-center gap-2">
              <Button asChild variant="ghost">
                <Link to="/sub-super-admin/user-list">Cancel</Link>
              </Button>
              <Button
                type="button"
                disabled={
                  register.isPending ||
                  (stepper.current === 0 &&
                    (usernameStatus === 'unavailable' || usernameStatus === 'checking'))
                }
                onClick={async () => {
                  if (!stepper.isLast) {
                    await handleNext();
                  } else {
                    form.handleSubmit(onSubmit)();
                  }
                }}
              >
                {register.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
                {stepper.isLast ? 'Create user' : <>Next <ArrowRight className="h-4 w-4" /></>}
              </Button>
            </div>
          </div>
        </form>
      </Form>
    </ScrollPage>
  );
}

/* Read-only summary on the final step. */
function ReviewStep({
  form,
  aclLookup,
}: {
  form: ReturnType<typeof useForm<CreateSubSuperAdminInput>>;
  aclLookup: Record<string, string>;
}) {
  const v = form.getValues();
  const expiry = v.expiryDate ? formatDate(new Date(v.expiryDate)) : '—';
  return (
    <Card>
      <CardHeader>
        <CardTitle>Review</CardTitle>
        <CardDescription>
          Double-check the details. Click any earlier step header to make changes.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <ReviewSection title="Account">
          <Row label="Username" value={v.username} />
          <Row label="Full name" value={v.name} />
          <Row label="Email" value={v.emailid} />
          <Row label="Phone" value={v.number} />
        </ReviewSection>
        <ReviewSection title="Organization">
          <Row label="Company" value={v.company} />
          <Row label="Address" value={v.address} multiline />
          <Row label="Pincode" value={v.pincode} />
        </ReviewSection>
        <ReviewSection title="Access">
          <Row label="Account type" value={v.accountType === '3' ? 'Both' : v.accountType === '1' ? 'Transactional' : 'Promotional'} />
          <Row label="Validity expires" value={expiry} />
          <Row label="ACL setting" value={aclLookup[v.aclId] ?? `#${v.aclId}`} />
        </ReviewSection>
      </CardContent>
    </Card>
  );
}

function ReviewSection({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <h3 className="mb-2 text-2xs font-semibold uppercase tracking-wider text-muted-foreground">
        {title}
      </h3>
      <dl className="grid grid-cols-1 gap-x-6 gap-y-2 md:grid-cols-2">{children}</dl>
    </div>
  );
}

function Row({ label, value, multiline }: { label: string; value?: string; multiline?: boolean }) {
  return (
    <div className={multiline ? 'md:col-span-2' : undefined}>
      <dt className="text-xs text-muted-foreground">{label}</dt>
      <dd
        className={`text-sm font-medium text-foreground ${multiline ? 'whitespace-pre-line' : ''}`}
      >
        {value || '—'}
      </dd>
    </div>
  );
}

function readErrorMessage(err: unknown): string | undefined {
  if (!err) return undefined;
  if (err instanceof Error) return err.message;
  if (typeof err === 'object' && err !== null) {
    const a = err as { response?: { data?: { message?: string } }; message?: string };
    return a.response?.data?.message ?? a.message;
  }
  return undefined;
}
